import { Any } from '@common/defs/types';
import { frFR } from '@mui/x-data-grid/locales';

const useUtils = () => {
  const createURLSearchParams = (params: { [key: string]: Any }): string => {
    const searchParams = new URLSearchParams();

    Object.keys(params).forEach((key) => {
      const value = params[key];
      if (Array.isArray(value)) {
        value
          .filter((item) => item !== undefined && item !== null)
          .forEach((item) => searchParams.append(`${key}[]`, String(item)));
      } else if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });

    return searchParams.toString();
  };

  const prettyNumber = (value: number, decimals = 2) => {
    return (Math.floor(value * 100) / 100).toFixed(decimals);
  };
  const countLabel = (value: number, singular: string, plural: string) => {
    return `${value} ${value <= 1 ? singular : plural}`;
  };
  const decimalFormat = (value: number) => {
    const decimalsFormatter = Intl.NumberFormat('fr-FR', {
      style: 'decimal',
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    });
    return decimalsFormatter.format(value);
  };

  const getDataGridlocalizedText = () => {
    return frFR.components.MuiDataGrid.defaultProps.localeText;
  };

  const isFullUrl = (url: string) => {
    return /^https?:\/\//.test(url);
  };

  const validateFrenchPhone = (value: string | undefined): boolean => {
    if (!value) {
      return false;
    }

    if (!value.startsWith('+33')) {
      return false;
    }

    const digitsOnly = value.replace(/\D/g, '');
    const phoneNumberDigits = digitsOnly.substring(2);

    if (phoneNumberDigits.startsWith('0')) {
      return false;
    }

    return phoneNumberDigits.length === 9;
  };

  const formatFrenchPhone = (value: string): string => {
    let cleaned = value.replace(/[^\d+\s]/g, '');

    if (!cleaned || cleaned === '') {
      return '+33';
    }

    if (!cleaned.startsWith('+33')) {
      const digitsOnly = cleaned.replace(/\D/g, '');
      if (digitsOnly.startsWith('33')) {
        cleaned = '+' + digitsOnly;
      } else if (digitsOnly.startsWith('0')) {
        cleaned = '+33' + digitsOnly.substring(1);
      } else {
        cleaned = '+33' + digitsOnly;
      }
    }

    const digitsOnly = cleaned.replace(/\D/g, '');
    let phoneNumberDigits = digitsOnly.substring(2);

    if (phoneNumberDigits.startsWith('0')) {
      phoneNumberDigits = phoneNumberDigits.substring(1);
    }

    if (phoneNumberDigits.length > 9) {
      phoneNumberDigits = phoneNumberDigits.substring(0, 9);
    }

    if (phoneNumberDigits.length >= 1) {
      const formatted = `+33 ${phoneNumberDigits.slice(0, 1)} ${phoneNumberDigits.slice(
        1,
        3
      )} ${phoneNumberDigits.slice(3, 5)} ${phoneNumberDigits.slice(
        5,
        7
      )} ${phoneNumberDigits.slice(7, 9)}`.trim();
      return formatted;
    }

    return '+33';
  };

  const generateRandomLetters = (length: number): string => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return result;
  };

  const generateRandomDigits = (length: number): string => {
    const characters = '0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return result;
  };

  const logToServer = (message: string, context: Any) => {
    fetch('/api/log', {
      method: 'POST',
      body: JSON.stringify({ message, context }),
    });
  };

  return {
    createURLSearchParams,
    prettyNumber,
    countLabel,
    decimalFormat,
    getDataGridlocalizedText,
    isFullUrl,
    validateFrenchPhone,
    formatFrenchPhone,
    generateRandomLetters,
    generateRandomDigits,
    logToServer,
  };
};

export default useUtils;
